import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar } from 'lucide-react';
import moment from 'moment';
import { PRESET } from './dateRange';

const CustomRangeInput = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className="flex items-center gap-2 min-h-[44px] px-4 border border-[#526477] rounded-md text-sm font-semibold text-[#111827] bg-white hover:bg-gray-50"
  >
    <Calendar size={18} className="text-[#526477]" aria-hidden="true" />
    {value || 'Pick dates'}
  </button>
));
CustomRangeInput.displayName = 'CustomRangeInput';

/**
 * Yesterday (default) | Today | Lifetime | Custom range.
 * `value` is { preset, from, to } with Date objects for a custom range.
 */
const DateRangeControl = ({ value, onChange, allowLifetime = true, maxDays }) => {
  const presets = [
    { key: PRESET.YESTERDAY, label: 'Yesterday' },
    { key: PRESET.TODAY, label: 'Today' },
    ...(allowLifetime ? [{ key: PRESET.LIFETIME, label: 'Lifetime' }] : []),
    { key: PRESET.CUSTOM, label: 'Custom' }
  ];

  const [start, setStart] = React.useState(value.from);
  const [end, setEnd] = React.useState(value.to);

  const pick = (preset) => {
    if (preset === PRESET.CUSTOM) {
      // Open on yesterday so the first pick is one click from a sensible range.
      const yesterday = moment().subtract(1, 'day').toDate();
      const from = value.from || yesterday;
      const to = value.to || yesterday;
      setStart(from);
      setEnd(to);
      onChange({ preset, from, to });
      return;
    }
    onChange({ preset, from: null, to: null });
  };

  const onRange = ([from, to]) => {
    setStart(from);
    setEnd(to);
    if (from && to) onChange({ preset: PRESET.CUSTOM, from, to });
  };

  const today = new Date();
  const maxDate =
    maxDays && start && !end
      ? moment.min(moment(start).add(maxDays - 1, 'days'), moment(today)).toDate()
      : today;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="group"
        aria-label="Date range"
        className="flex border border-[#526477] rounded-md overflow-hidden bg-white"
      >
        {presets.map((p, i) => {
          const active = value.preset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={active}
              onClick={() => pick(p.key)}
              className={`min-h-[44px] px-4 text-sm font-semibold transition-colors ${
                i > 0 ? 'border-l border-slate-300' : ''
              } ${active ? 'bg-[#1F4B55] text-white' : 'text-[#111827] hover:bg-gray-50'}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {value.preset === PRESET.CUSTOM && (
        <DatePicker
          selectsRange
          startDate={start}
          endDate={end}
          onChange={onRange}
          maxDate={maxDate}
          dateFormat="d MMM yyyy"
          customInput={<CustomRangeInput />}
          popperPlacement="bottom-end"
          popperClassName="!z-[60]"
        />
      )}
    </div>
  );
};

export default DateRangeControl;
